// features/tutorials/components/ModuleCard.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, BookOpen, CheckCircle, Play, ChevronDown, Trophy } from 'lucide-react';
import type { TutorialModule, TutorialLesson } from '../types';

interface ModuleCardProps {
    module: TutorialModule;
    progress: number;
    isExpanded: boolean;
    isCompleted: boolean;
    onModuleClick: () => void;
    onLessonClick: (lesson: TutorialLesson) => void;
    isLessonCompleted: (lessonId: string) => boolean;
}

// Color mapping for gradient strokes
const colorMap: Record<string, { from: string; to: string }> = {
    'from-amber-400 to-orange-500': { from: '#F59E0B', to: '#F97316' },
    'from-teal-400 to-cyan-500': { from: '#2DD4BF', to: '#06B6D4' },
    'from-blue-400 to-indigo-500': { from: '#60A5FA', to: '#6366F1' },
    'from-purple-400 to-violet-500': { from: '#A78BFA', to: '#8B5CF6' },
    'from-rose-400 to-pink-500': { from: '#FB7185', to: '#EC4899' },
    'from-emerald-400 to-green-500': { from: '#34D399', to: '#10B981' },
};

export const ModuleCard: React.FC<ModuleCardProps> = ({
    module,
    progress,
    isExpanded,
    isCompleted,
    onModuleClick,
    onLessonClick,
    isLessonCompleted,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const gradientId = `progress-gradient-${module.id}`;

    const totalLessons = module.lessons.length;
    const completedLessons = module.lessons.filter((l: TutorialLesson) => isLessonCompleted(l.id)).length;

    const circumference = 2 * Math.PI * 35;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const colors = colorMap[module.color] || { from: '#60A5FA', to: '#6366F1' };

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ y: -8, scale: 1.02 }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            className="relative"
        >
            {/* Glow Effect */}
            <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${module.color} rounded-3xl blur-xl transition-opacity duration-300`}
                animate={{ opacity: isHovered ? 0.25 : 0 }}
            />

            <div className="relative bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-3xl border border-slate-200/50 dark:border-slate-700/50 overflow-hidden hover:shadow-2xl transition-all duration-300">
                {/* Gradient Border Top */}
                <div className={`h-1.5 bg-gradient-to-r ${module.color}`} />

                <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                        {/* Icon with Rotation Animation */}
                        <motion.div
                            animate={isHovered ? { rotateY: [0, 10, 0, -10, 0] } : {}}
                            transition={{ duration: 1, repeat: isHovered ? Infinity : 0, repeatDelay: 2 }}
                            className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${module.color} flex items-center justify-center shadow-lg`}
                        >
                            <module.icon className="w-8 h-8 text-white" />
                        </motion.div>

                        {/* Progress Ring */}
                        <div className="relative w-20 h-20">
                            <svg className="w-20 h-20 transform -rotate-90">
                                <defs>
                                    <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
                                        <stop offset="0%" stopColor={colors.from} />
                                        <stop offset="100%" stopColor={colors.to} />
                                    </linearGradient>
                                </defs>
                                {/* Background circle */}
                                <circle
                                    cx="40"
                                    cy="40"
                                    r="35"
                                    stroke="currentColor"
                                    strokeWidth="6"
                                    fill="transparent"
                                    className="text-slate-200 dark:text-slate-700"
                                />
                                {/* Progress circle */}
                                <motion.circle
                                    cx="40"
                                    cy="40"
                                    r="35"
                                    stroke={`url(#${gradientId})`}
                                    strokeWidth="6"
                                    fill="transparent"
                                    strokeLinecap="round"
                                    initial={{ strokeDashoffset: circumference }}
                                    animate={{ strokeDashoffset }}
                                    transition={{ duration: 1.5, ease: 'easeInOut' }}
                                    style={{
                                        strokeDasharray: circumference,
                                    }}
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                {isCompleted ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    >
                                        <Trophy className="w-6 h-6 text-amber-500" />
                                    </motion.div>
                                ) : (
                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                                        {Math.round(progress)}%
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Module Info */}
                    <div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
                            {module.title}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                            {module.subtitle}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400 mb-4">
                            <span className="flex items-center">
                                <BookOpen className="w-4 h-4 mr-1" />
                                {totalLessons} lessons
                            </span>
                            <span className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                ~{module.estimatedHours}h
                            </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
                            <motion.div
                                className={`h-full bg-gradient-to-r ${module.color} rounded-full`}
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 1, ease: 'easeOut' }}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {completedLessons} of {totalLessons} completed
                            </span>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onModuleClick();
                                }}
                                className={`px-4 py-2 rounded-xl bg-gradient-to-r ${module.color} text-white font-medium flex items-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-300`}
                            >
                                <span>{progress > 0 ? 'Continue' : 'Start'}</span>
                                <motion.div
                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <ChevronDown className="w-4 h-4" />
                                </motion.div>
                            </motion.button>
                        </div>
                    </div>
                </div>

                {/* Expandable Lesson List */}
                <AnimatePresence>
                    {isExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                        >
                            <div className="border-t border-slate-200 dark:border-slate-700 p-4">
                                <div className="space-y-2">
                                    {module.lessons.map((lesson: TutorialLesson, index: number) => (
                                        <motion.button
                                            key={lesson.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            whileHover={{ x: 5 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onLessonClick(lesson);
                                            }}
                                            className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLessonCompleted(lesson.id)
                                                    ? 'bg-green-100 dark:bg-green-900/30'
                                                    : 'bg-slate-100 dark:bg-slate-700'
                                                    }`}>
                                                    {isLessonCompleted(lesson.id) ? (
                                                        <motion.div
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            transition={{ type: 'spring', stiffness: 300 }}
                                                        >
                                                            <CheckCircle className="w-5 h-5 text-green-500" />
                                                        </motion.div>
                                                    ) : (
                                                        <Play className="w-5 h-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
                                                    )}
                                                </div>
                                                <div className="text-left">
                                                    <div className="font-medium text-slate-900 dark:text-white text-sm">
                                                        {lesson.title}
                                                    </div>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                                        {lesson.duration} • {lesson.difficulty}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${lesson.difficulty === 'beginner'
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : lesson.difficulty === 'intermediate'
                                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                                    : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                                                }`}>
                                                {lesson.difficulty}
                                            </span>
                                        </motion.button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};